import {
  AdLicenseDto,
  AdLicenseQuoteDto,
  AdLicenseSlotState,
} from '../../common/general-types/ad-license.dto';
import { VideoStatusEnum } from '../../common/general-types/videos.dto';

/**
 * Which licence slot a video card shows.
 *
 * An ineligible (or not-yet-loaded) quote hides the slot entirely. A declined, cancelled or
 * unanswered request falls back to the Buy CTA, so the brand can ask again.
 */
export function resolveAdLicenseSlot(
  videoStatus: number,
  quote: AdLicenseQuoteDto | null | undefined,
  license: AdLicenseDto | null | undefined,
): AdLicenseSlotState {
  if (license?.displayStatus === 'pending') return 'pending';
  if (license?.displayStatus === 'licensed') return 'licensed';
  if (!quote?.eligible) return 'hidden';
  if (videoStatus !== VideoStatusEnum.Accepted) return 'locked';
  if (license?.displayStatus === 'expired') return 'expired';
  return 'canBuy';
}

/** Whether the video counts towards the "Licensed" filter (pending or active). */
export function isLicensedOrPending(license: AdLicenseDto | null | undefined): boolean {
  return license?.displayStatus === 'pending' || license?.displayStatus === 'licensed';
}

/** A declined attempt the brand should see the reason for, next to the Buy CTA. */
export function lastDeclinedLicense(license: AdLicenseDto | null | undefined): AdLicenseDto | null {
  return license?.displayStatus === 'declined' ? license : null;
}

/** Index licences by video, keeping the newest when the API returns more than one per video. */
export function indexLicensesByVideo(licenses: AdLicenseDto[]): Record<string, AdLicenseDto> {
  const byVideo: Record<string, AdLicenseDto> = {};
  for (const license of licenses) {
    const existing = byVideo[license.videoId];
    if (!existing || new Date(license.requestedAt).getTime() > new Date(existing.requestedAt).getTime()) {
      byVideo[license.videoId] = license;
    }
  }
  return byVideo;
}
